import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../utils/axios';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';
import FileUpload from '../components/FileUpload';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Package,
  Tag,
  Eye,
  EyeOff,
  Grid,
  List,
  MoreVertical,
  DollarSign,
  Star,
  Clock,
  Hash,
  X,
  Settings,
  Layers,
} from 'lucide-react';

const MenuAddons = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMenu, setSelectedMenu] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [filterAvailability, setFilterAvailability] = useState('all'); // all, available, unavailable
  const [sortBy, setSortBy] = useState('name'); // name, price, created_at
  const queryClient = useQueryClient();

  const { data: menuItems, isLoading: menuItemsLoading } = useQuery(
    ['menu-items', selectedMenu, selectedCategory, sortBy],
    async () => {
      const params = {};
      if (selectedMenu) params.menu_id = selectedMenu;
      if (selectedCategory) params.category_id = selectedCategory;
      if (sortBy) params.sort_by = sortBy;
      const response = await api.get('/vendor/menu-items', { params });
      return Array.isArray(response.data) ? response.data : [];
    }
  );

  const { data: addons, isLoading: addonsLoading } = useQuery(
    ['menu-addons', selectedMenuItem?.id],
    async () => {
      if (!selectedMenuItem) return [];
      const response = await api.get(`/api/menu-items/${selectedMenuItem.id}/addons`);
      return response.data.addons || [];
    },
    { enabled: !!selectedMenuItem }
  );

  const { data: menus } = useQuery(
    ['menus'],
    async () => {
      const response = await api.get('/vendor/menus');
      return Array.isArray(response.data) ? response.data : [];
    }
  );

  const { data: categories } = useQuery(
    ['menu-categories', selectedMenu],
    async () => {
      if (!selectedMenu) return [];
      const response = await api.get(`/vendor/menus/${selectedMenu}/categories`);
      return Array.isArray(response.data) ? response.data : [];
    },
    { enabled: !!selectedMenu }
  );

  const createAddonMutation = useMutation(
    async (addonData) => {
      const response = await api.post(`/api/menu-items/${selectedMenuItem.id}/addons`, addonData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['menu-addons']);
        showSuccessToast('Add-on created successfully!', 'addon-create');
        setShowCreateModal(false);
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to create add-on', 'addon-create');
      },
    }
  );

  const updateAddonMutation = useMutation(
    async ({ addonId, data }) => {
      const response = await api.put(`/api/menu-items/${selectedMenuItem.id}/addons/${addonId}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['menu-addons']);
        showSuccessToast('Add-on updated successfully!', 'addon-update');
        setEditingAddon(null);
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to update add-on', 'addon-update');
      },
    }
  );

  const deleteAddonMutation = useMutation(
    async (addonId) => {
      await api.delete(`/api/menu-items/${selectedMenuItem.id}/addons/${addonId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['menu-addons']);
        showSuccessToast('Add-on deleted successfully!', 'addon-delete');
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to delete add-on', 'addon-delete');
      },
    }
  );

  const toggleAvailabilityMutation = useMutation(
    async (addonId) => {
      const addon = Array.isArray(addons) ? addons.find(a => a.id === addonId) : null;
      await api.put(`/api/menu-items/${selectedMenuItem.id}/addons/${addonId}`, {
        is_available: !addon.is_available
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['menu-addons']);
        showSuccessToast('Add-on availability updated!', 'addon-availability');
      },
      onError: (error) => {
        showErrorToast(error.response?.data?.message || 'Failed to update availability', 'addon-availability');
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const addonData = {
      name: formData.get('name'),
      description: formData.get('description'),
      price: parseFloat(formData.get('price')),
      is_required: formData.get('is_required') === 'true',
      max_quantity: parseInt(formData.get('max_quantity')) || 1,
      min_quantity: parseInt(formData.get('min_quantity')) || 0,
      is_available: formData.get('is_available') === 'true',
      image_url: formData.get('image_url') || null,
    };

    if (editingAddon) {
      updateAddonMutation.mutate({ addonId: editingAddon.id, data: addonData });
    } else {
      createAddonMutation.mutate(addonData);
    }
  };

  const filteredAddons = (Array.isArray(addons) ? addons : []).filter(addon => {
    const matchesSearch = addon.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         addon.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAvailability = filterAvailability === 'all' ||
      (filterAvailability === 'available' && addon.is_available) ||
      (filterAvailability === 'unavailable' && !addon.is_available);
    return matchesSearch && matchesAvailability;
  });

  const AddonModal = ({ addon = null, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">
            {addon ? 'Edit Add-on' : 'Create New Add-on'}
          </h2>
          <button
            onClick={onClose}
            className="text-secondary-400 hover:text-secondary-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Add-on Name *
            </label>
            <input
              type="text"
              name="name"
              defaultValue={addon?.name || ''}
              required
              className="input-field"
              placeholder="e.g., Extra Cheese"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              defaultValue={addon?.description || ''}
              rows="3"
              className="input-field"
              placeholder="Describe this add-on..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Add-on Image
            </label>
            {addon?.image_url && (
              <div className="mb-3">
                <p className="text-sm text-secondary-600 mb-2">Current Image:</p>
                <div className="relative inline-block">
                  <img 
                    src={addon.image_url} 
                    alt={addon.name}
                    className="h-20 w-20 object-cover rounded-lg border border-secondary-200"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              </div>
            )}
            <FileUpload
              onUploadSuccess={(file) => {
                const imageUrlInput = document.querySelector('input[name="image_url"]');
                if (imageUrlInput) {
                  imageUrlInput.value = file.url;
                }
              }}
              onUploadError={(error) => {
                console.error('Addon upload error:', error);
              }}
              multiple={false}
              placeholder="Upload add-on image"
              showPreview={true}
            />
            <input
              type="hidden"
              name="image_url"
              defaultValue={addon?.image_url || ''}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Price *
              </label>
              <div className="relative">
                <DollarSign className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                <input
                  type="number"
                  name="price"
                  defaultValue={addon?.price || ''}
                  min="0"
                  step="0.01"
                  required
                  className="input-field pl-10"
                  placeholder="0.00"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Max Quantity
              </label>
              <input
                type="number"
                name="max_quantity"
                defaultValue={addon?.max_quantity || 1}
                min="1"
                className="input-field"
                placeholder="1"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Min Quantity
            </label>
            <input
              type="number"
              name="min_quantity"
              defaultValue={addon?.min_quantity || 0}
              min="0"
              className="input-field"
              placeholder="0"
            />
          </div>
          
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_required"
                value="true"
                defaultChecked={addon?.is_required || false}
                className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-secondary-700">Required add-on</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_available"
                value="true"
                defaultChecked={addon?.is_available ?? true}
                className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-secondary-700">Available for ordering</span>
            </label>
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              className="btn-primary flex-1"
              disabled={createAddonMutation.isLoading || updateAddonMutation.isLoading}
            >
              {createAddonMutation.isLoading || updateAddonMutation.isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                addon ? 'Update Add-on' : 'Create Add-on'
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  if (menuItemsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className=" ">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Menu Item Add-ons</h1>
          <p className="text-secondary-600">
            Manage customization options for your menu items
          </p>
        </div>
        {selectedMenuItem && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Customization
          </button>
        )}
      </div>

      {/* Menu Item Selection */}
      <div className="card">
        <h3 className="text-lg font-medium text-secondary-900 mb-4">Select Menu Item</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <select
            value={selectedMenu}
            onChange={(e) => {
              setSelectedMenu(e.target.value);
              setSelectedCategory('');
              setSelectedMenuItem(null);
            }}
            className="input-field"
          >
            <option value="">All Menus</option>
            {menus?.map((menu) => (
              <option key={menu.id} value={menu.id}>
                {menu.type} Menu
              </option>
            ))}
          </select>
          
          <select
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setSelectedMenuItem(null);
            }}
            className="input-field"
            disabled={!selectedMenu}
          >
            <option value="">All Categories</option>
            {categories?.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          
          <select
            value={selectedMenuItem?.id || ''}
            onChange={(e) => {
              const item = menuItems?.find(item => item.id == e.target.value);
              setSelectedMenuItem(item || null);
            }}
            className="input-field"
            disabled={!selectedMenu}
          >
            <option value="">Select Menu Item</option>
            {menuItems?.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name} - £{item.price}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Add-ons Management */}
      {selectedMenuItem && (
        <>
          {/* Selected Item Info */}
          <div className="card bg-primary-50 border-primary-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-primary-900">{selectedMenuItem.name}</h3>
                <p className="text-primary-700">{selectedMenuItem.description}</p>
                <p className="text-sm text-primary-600">Base Price: £{selectedMenuItem.price}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-primary-600">Menu: {selectedMenuItem.menu?.type}</p>
                <p className="text-sm text-primary-600">Category: {selectedMenuItem.menuCategory?.name || 'No category'}</p>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="card">
            <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
                  <input
                    type="text"
                    placeholder="Search add-ons..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input-field pl-10"
                  />
                </div>
                
                <select
                  value={filterAvailability}
                  onChange={(e) => setFilterAvailability(e.target.value)}
                  className="input-field"
                >
                  <option value="all">All Add-ons</option>
                  <option value="available">Available Only</option>
                  <option value="unavailable">Unavailable Only</option>
                </select>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-primary-100 text-primary-600' : 'text-secondary-400'}`}
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'text-secondary-400'}`}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Add-ons Grid/List */}
          {addonsLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAddons.map((addon) => (
                <div key={addon.id} className="card hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="h-12 w-12 bg-secondary-100 rounded-lg flex items-center justify-center overflow-hidden">
                      {addon.image_url ? (
                        <img 
                          src={addon.image_url} 
                          alt={addon.name}
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className={`h-full w-full flex items-center justify-center ${addon.image_url ? 'hidden' : ''}`}>
                        <Layers className="h-6 w-6 text-secondary-600" />
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => toggleAvailabilityMutation.mutate(addon.id)}
                        className={`p-1 rounded ${addon.is_available ? 'text-green-600' : 'text-gray-400'}`}
                        title={addon.is_available ? 'Available' : 'Unavailable'}
                      >
                        {addon.is_available ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2">{addon.name}</h3>
                  
                  {addon.description && (
                    <p className="text-sm text-secondary-600 mb-3">{addon.description}</p>
                  )}
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-secondary-600">Price:</span>
                      <span className="font-medium text-secondary-900">£{addon.price}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-secondary-600">Quantity:</span>
                      <span className="font-medium">{addon.min_quantity}-{addon.max_quantity}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-secondary-600">Required:</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        addon.is_required ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {addon.is_required ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setEditingAddon(addon)}
                      className="btn-primary flex-1 text-sm"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => deleteAddonMutation.mutate(addon.id)}
                      className="btn-secondary flex-1 text-sm text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-secondary-200">
                      <th className="text-left py-3 px-4 font-medium text-secondary-700">Add-on</th>
                      <th className="text-left py-3 px-4 font-medium text-secondary-700">Price</th>
                      <th className="text-left py-3 px-4 font-medium text-secondary-700">Quantity Range</th>
                      <th className="text-left py-3 px-4 font-medium text-secondary-700">Required</th>
                      <th className="text-left py-3 px-4 font-medium text-secondary-700">Status</th>
                      <th className="text-left py-3 px-4 font-medium text-secondary-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAddons.map((addon) => (
                      <tr key={addon.id} className="border-b border-secondary-100 hover:bg-secondary-50">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-secondary-900">{addon.name}</p>
                            {addon.description && (
                              <p className="text-sm text-secondary-600">{addon.description}</p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-medium text-secondary-900">£{addon.price}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm text-secondary-600">
                            {addon.min_quantity}-{addon.max_quantity}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            addon.is_required ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {addon.is_required ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            addon.is_available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {addon.is_available ? 'Available' : 'Unavailable'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => toggleAvailabilityMutation.mutate(addon.id)}
                              className={`${addon.is_available ? 'text-green-600' : 'text-red-600'} hover:text-gray-600`}
                              title={addon.is_available ? 'Hide Add-on' : 'Show Add-on'}
                            >
                              {addon.is_available ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                            </button>
                            <button
                              onClick={() => setEditingAddon(addon)}
                              className="text-secondary-600 hover:text-secondary-700"
                              title="Edit Add-on"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => deleteAddonMutation.mutate(addon.id)}
                              className="text-red-600 hover:text-red-700"
                              title="Delete Add-on"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Empty State */}
          {filteredAddons.length === 0 && (
            <div className="text-center py-12">
              <Layers className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 mb-2">No add-ons found</h3>
              <p className="text-secondary-600 mb-4">
                {searchTerm || filterAvailability !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Get started by adding customization options for this menu item'
                }
              </p>
              {!searchTerm && filterAvailability === 'all' && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="btn-primary"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Add-on
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* No Item Selected State */}
      {!selectedMenuItem && (
        <div className="text-center py-12">
          <Package className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-secondary-900 mb-2">Select a Menu Item</h3>
          <p className="text-secondary-600">
            Choose a menu item from the dropdown above to manage its customization options
          </p>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <AddonModal onClose={() => setShowCreateModal(false)} />
      )}
      
      {editingAddon && (
        <AddonModal addon={editingAddon} onClose={() => setEditingAddon(null)} />
      )}
    </div>
  );
};

export default MenuAddons; 